import type { SearchNotesQuery } from '.'
import type {
  SearchObjectFieldsTypeFromMappingObjectType,
  SearchResult,
  SearchResponse,
} from './types'
import { noteFragmentsMapping } from '../indexMappings/noteFragments'
import { buildUrl } from '../utils/build-url'
import { fetchThrowsIfNotOk } from '../utils/fetch-throws-if-not-ok'

type SearchNoteFragmentsFields = SearchObjectFieldsTypeFromMappingObjectType<
  typeof noteFragmentsMapping
>

export type SearchNoteFragmentsRequiredFields = Required<{
  [K in keyof Pick<
    SearchNoteFragmentsFields,
    | 'fullPath'
    | 'noteId'
    | 'title'
    | 'noteId'
    | 'channelId'
    | 'channelType'
    | 'iconShape'
    | 'iconColor'
    | 'isArchived'
    | 'type'
    | 'fragmentRange'
    | 'fragmentId'
    | 'externalType'
    | 'externalUrl'
  >]: SearchNoteFragmentsFields[K]
}>

// Only used to have type safety and SEARCH_NOTE_FRAGMENTS_REQUIRED_FIELDS matching SearchRequiredFields type definition
const searchNoteFragmentsRequiredFieldsHelper: {
  [K in keyof SearchNoteFragmentsRequiredFields]: true
} = {
  fullPath: true,
  noteId: true,
  title: true,
  channelId: true,
  channelType: true,
  iconColor: true,
  iconShape: true,
  isArchived: true,
  type: true,
  fragmentId: true,
  fragmentRange: true,
  externalType: true,
  externalUrl: true,
}

export const SEARCH_NOTE_FRAGMENTS_REQUIRED_FIELDS = Object.freeze(
  Object.keys(searchNoteFragmentsRequiredFieldsHelper)
)

export type SearchNoteFragmentsResult =
  SearchResult<SearchNoteFragmentsRequiredFields>

export type SearchNoteFragmentsResponse =
  SearchResponse<SearchNoteFragmentsResult>

export async function fetchNoteFragments<
  T extends SearchNoteFragmentsRequiredFields = SearchNoteFragmentsRequiredFields
>({
  baseUrl,
  token,
  query,
  highlightPostTag,
  highlightPreTag,
  hitsPerPage,
  optionalFilters,
  page,
  fields = [],
}: {
  baseUrl: string
  token: string
  fields?: string[]
} & SearchNotesQuery): Promise<SearchResponse<SearchResult<T>>> {
  const response = await fetchThrowsIfNotOk(
    buildUrl(baseUrl, '/note-fragments'),
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      method: 'post',
      body: JSON.stringify({
        query,
        optionalFilters,
        highlightPostTag,
        highlightPreTag,
        hitsPerPage,
        page,
        fields,
      }),
    }
  )

  return await response.json()
}

export enum FragmentTypes {
  PDF = 'pdf',
  DATABASE = 'database',
}
